import * as React from 'react';
import { useMemo } from 'react';
import { Alert } from 'reactstrap';
import { useTranslations, getLanguage } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { CommunicationQuotas, CommunicationChannel, RemindersSettingsData } from '../../../models';
import Typography from '../../../components/Typography';
import { useDispatch } from 'react-redux';
import { alertError, alertGenericError, alertSuccess } from '../../../store/alerts/actions';
import { testReminderSmsAsync, testReminderWhatsAppAsync } from '../../../ajax/requests';
import { getErrorMessage, isConflictError } from '../../../ajax/errors';
import { SegmentedMessage } from 'sms-segments-calculator';
import { CheckInputField } from '../../../components/Forms/fields/CheckInputField';
import { PlaceholderTextField, TextInputField } from '../../../components/Forms/fields';
import { NumberInputField } from '../../../components/Forms/fields';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import HelpIcon from '@material-ui/icons/Help';
import { paths } from '../../../app/constants';
import {
    ChannelCard, ChannelHeader, ChannelInfo, ChannelContent, ChannelStatus, ChannelQuota,
    ChannelWarning, MySubscriptionLink, TestSection, getMessageIcon, buildTestData,
} from './remindersShared';
import AppButton from '../../../components/AppButton';

const KNOWN_DEFAULTS = {
    confirm: [TK.confirmationResponseYes],
    reject: [TK.confirmationResponseNo, TK.confirmationResponseCancel],
};

const normalizeTokens = (values: string[]) =>
    values.map(value => value.trim().toLowerCase()).filter(Boolean);

// Helper to check if array matches known defaults (case-insensitive, order-agnostic)
const matchesDefaults = (arr: string[], defaults: string[]) => {
    const normalized = normalizeTokens(arr);
    const normalizedDefaults = normalizeTokens(defaults);
    if (normalized.length !== normalizedDefaults.length) return false;
    const normalizedSet = new Set(normalized);
    return normalizedDefaults.every(value => normalizedSet.has(value));
};

export interface ConfirmationTabProps {
    values: RemindersSettingsData;
    onChange: (reminders: RemindersSettingsData) => void;
    communicationQuotas: CommunicationQuotas | null;
}

const ConfirmationTab: React.FC<ConfirmationTabProps> = ({ values, onChange, communicationQuotas }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();
    const culture = getLanguage();
    const privacyPolicyUrl = `https://appointmint.pt/${culture}/privacy`;
    const [whatsAppHelpContext, setWhatsAppHelpContext] = React.useState<'confirmation' | null>(null);

    const [expandedConfirmationChannel, setExpandedConfirmationChannel] = React.useState<CommunicationChannel | null>(() => {
        const channels = values.appointmentConfirmation?.channels;
        if (channels?.length) return channels[0];
        return null;
    });

    const [phoneNumberTest, setPhoneTest] = React.useState('');
    const [testing, setTesting] = React.useState(false);

    const companyName = generalSettings.companyName ?? '';

    const getTranslationWithFallback = React.useCallback((key: TK, fallback: string) => {
        const translated = t(key);
        return translated === key ? fallback : translated;
    }, [t]);

    const whatsAppDisclaimerStart = getTranslationWithFallback(
        TK.whatsAppMetaReviewDisclaimerStart,
        'Appointmint uses its own phone number'
    );
    const whatsAppDisclaimerEnd = getTranslationWithFallback(
        TK.whatsAppMetaReviewDisclaimerEnd,
        'to communicate with your clients by WhatsApp, send reminders and confirmations, receive responses, and all communications are visible in the client profile communication chat.'
    );
    const whatsAppConfirmationDefaultTemplate = getTranslationWithFallback(
        TK.whatsAppConfirmationDefaultTemplate,
        `Hi {{client_name}}!
{{company_name}} reminds you of your {{appointment_type}} appointment with {{professional_name}} on {{day}} ({{week_day}}), at {{time}}.
Please reply Yes to confirm or No if you cannot attend.
Thank you and see you soon!`
    );

    const smsConfirmationDefaultTemplate = useMemo(() =>
        getTranslationWithFallback(
            TK.appointmentConfirmationDefaultMessage,
            `Hi {client-name}! {appointment-type} with {professional-name} on {date} at {time}. Reply YES to confirm or NO to cancel.`
        ).replace(/{companyName}/g, companyName),
    [getTranslationWithFallback, companyName]);

    const confirmationFallbackDisclaimerText = t(TK.confirmationWhatsAppSmsFallbackDisclaimer);

    const confirmationDefaults = useMemo(() => ({
        confirm: KNOWN_DEFAULTS.confirm.map(key => t(key)),
        reject: KNOWN_DEFAULTS.reject.map(key => t(key)),
    }), [t]);

    const normalizeConfirmationSettings = React.useCallback((
        confirmation: typeof values.appointmentConfirmation,
        fallbackName?: string,
        fallbackMessage?: string
    ) => {
        const confirmResponses = confirmation?.responses?.confirm?.length
            ? confirmation.responses.confirm
            : confirmationDefaults.confirm;
        const rejectResponses = confirmation?.responses?.reject?.length
            ? confirmation.responses.reject
            : confirmationDefaults.reject;
        const useDefaults = matchesDefaults(confirmResponses, confirmationDefaults.confirm) &&
            matchesDefaults(rejectResponses, confirmationDefaults.reject);
        const responses = useDefaults
            ? confirmationDefaults
            : { confirm: confirmResponses, reject: rejectResponses };

        return {
            channels: confirmation?.channels ?? [],
            name: (confirmation?.name?.trim()) || fallbackName || '',
            message: (confirmation?.message?.trim()) || fallbackMessage || '',
            hoursBeforeAppointment: confirmation?.hoursBeforeAppointment ?? 24,
            responses,
        };
    }, [confirmationDefaults]);

    const appointmentConfirmation = useMemo(() =>
        normalizeConfirmationSettings(
            values.appointmentConfirmation,
            values.smsFrom ?? generalSettings.companyName ?? t(TK.appointmentConfirmationDefaultName),
            smsConfirmationDefaultTemplate
        )
    , [values.appointmentConfirmation, values.smsFrom, normalizeConfirmationSettings, generalSettings.companyName, t, smsConfirmationDefaultTemplate]);

    const confirmationSmsNrSegments = useMemo(() => new SegmentedMessage(appointmentConfirmation.message ?? '').segmentsCount, [appointmentConfirmation.message]);

    const buildUsageLabel = React.useCallback((used?: number, allowed?: number) =>
        used !== undefined && allowed !== undefined ? `${Math.min(used, allowed)}/${allowed}` : null
    , []);

    const smsUsageLabel = buildUsageLabel(communicationQuotas?.smsUsed, communicationQuotas?.smsAllowed);
    const whatsAppUsed = communicationQuotas?.whatsAppUsed;
    const whatsAppAllowed = communicationQuotas?.whatsAppAllowed;
    const whatsAppUsageLabel = buildUsageLabel(whatsAppUsed, whatsAppAllowed);
    const whatsAppQuotaExhausted = whatsAppUsed !== undefined
        && whatsAppAllowed !== undefined
        && whatsAppUsed >= whatsAppAllowed;

    const getConfirmationChannelUsageLabel = React.useCallback((channel: CommunicationChannel) => {
        if (channel === CommunicationChannel.SMS) return smsUsageLabel;
        if (channel === CommunicationChannel.WhatsApp) return whatsAppUsageLabel;
        return null;
    }, [smsUsageLabel, whatsAppUsageLabel]);

    const catchRequestError = React.useCallback((error: unknown) => {
        const message = getErrorMessage(error);
        if (message) {
            dispatch(alertError(message));
            return;
        }

        dispatch(isConflictError(error)
            ? alertError(t(TK.plafontExausted))
            : alertGenericError())
    }, [dispatch, t]);

    const onChangeReminders = (reminders: RemindersSettingsData) => onChange(reminders);

    const updateAppointmentConfirmation = (updates: Partial<typeof appointmentConfirmation>) => {
        const current = appointmentConfirmation;
        const nextConfirmation = {
            ...current,
            ...updates,
            responses: updates.responses ?? current.responses ?? confirmationDefaults,
            channels: updates.channels ?? current.channels ?? [],
            hoursBeforeAppointment: updates.hoursBeforeAppointment ?? current.hoursBeforeAppointment ?? 24,
        };

        if (!nextConfirmation.hoursBeforeAppointment) {
            nextConfirmation.hoursBeforeAppointment = 24;
        }

        onChangeReminders({
            ...values,
            appointmentConfirmation: nextConfirmation
        });
    };

    const updateResponses = (type: 'confirm' | 'reject', value: string) => {
        const tokens = value.split(',').map(v => v.trim()).filter(Boolean);
        const responses = {
            ...appointmentConfirmation.responses,
            [type]: tokens.length ? tokens : confirmationDefaults[type],
        };
        updateAppointmentConfirmation({ responses });
    };

    const handleTestConfirmationSms = React.useCallback(() => {
        if(!/^((\+\d{12})|(\d{9}))$/.test(phoneNumberTest)) {
            dispatch(alertError(t(TK.invalidPhoneNumber)));
            return;
        }
        setTesting(true);
        const confirmationTestData = buildTestData({
            ...values,
            appointmentConfirmation: appointmentConfirmation,
        }, phoneNumberTest, true);
        testReminderSmsAsync(confirmationTestData)
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchRequestError)
            .finally(() => setTesting(false));
    }, [phoneNumberTest, values, appointmentConfirmation, dispatch, catchRequestError, t]);

    const handleTestWhatsApp = React.useCallback((isConfirmationRequest: boolean) => {
        if(!/^((\+\d{12})|(\d{9}))$/.test(phoneNumberTest)) {
            dispatch(alertError(t(TK.invalidPhoneNumber)));
            return;
        }
        setTesting(true);
        testReminderWhatsAppAsync(buildTestData(values, phoneNumberTest, isConfirmationRequest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchRequestError)
            .finally(() => setTesting(false));
    }, [phoneNumberTest, values, dispatch, catchRequestError, t]);

    React.useEffect(() => {
        if (appointmentConfirmation.channels?.length && !appointmentConfirmation.message) {
            updateAppointmentConfirmation({ message: smsConfirmationDefaultTemplate });
        }
    }, [appointmentConfirmation.channels, appointmentConfirmation.message, updateAppointmentConfirmation, smsConfirmationDefaultTemplate]);

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
            <Typography size="large"><b>{t(TK.communicationChannels)}</b></Typography>
            <Alert color="secondary" isOpen>
                {confirmationFallbackDisclaimerText}
            </Alert>

            <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)'}}>
                {[CommunicationChannel.SMS, CommunicationChannel.WhatsApp].map(channel => {
                    const isActive = appointmentConfirmation.channels?.includes(channel) ?? false;
                    const isExpanded = expandedConfirmationChannel === channel;
                    const isWhatsApp = channel === CommunicationChannel.WhatsApp;
                    const isWhatsAppCheckboxDisabled = isWhatsApp && whatsAppQuotaExhausted && !isActive;
                    const channelUsageLabel = getConfirmationChannelUsageLabel(channel);
                    return (
                        <ChannelCard key={channel} $isActive={isActive} $isSelected={isExpanded}>
                            <ChannelHeader
                                $isActive={isActive}
                                $isSelected={isExpanded}
                                onClick={() => {
                                    if (!isActive) return;
                                    setExpandedConfirmationChannel(isExpanded ? null : channel)
                                }}
                            >
                                <CheckInputField
                                    label=""
                                    value={isActive}
                                    disabled={isWhatsAppCheckboxDisabled}
                                    onChange={enabled => {
                                        const channels = appointmentConfirmation.channels || [];
                                        if (enabled && isWhatsAppCheckboxDisabled) return;
                                        const newChannels = enabled
                                            ? [...channels.filter(c => c !== channel), channel]
                                            : channels.filter(c => c !== channel);
                                        updateAppointmentConfirmation({ channels: newChannels });
                                        if (enabled) setExpandedConfirmationChannel(channel);
                                        if (!enabled && isExpanded) setExpandedConfirmationChannel(null);
                                    }}
                                    onClick={e => e.stopPropagation()}
                                />
                                <ChannelInfo>
                                    {channel === CommunicationChannel.SMS ? getMessageIcon('sms') : getMessageIcon('whatsapp')}
                                    <Typography>
                                        <b>
                                            <span style={{display: 'inline-flex', alignItems: 'center', gap: 'var(--spacer-XXS)', flexWrap: 'wrap'}}>
                                                <span>{channel === CommunicationChannel.SMS ? t(TK.sms) : 'WhatsApp'}</span>
                                                {channelUsageLabel && <ChannelQuota>({channelUsageLabel})</ChannelQuota>}
                                                {channel === CommunicationChannel.WhatsApp && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                            e.stopPropagation();
                                                            setWhatsAppHelpContext('confirmation');
                                                        }}
                                                        style={{padding: 2}}
                                                    >
                                                        <HelpIcon style={{fontSize: '1rem'}} />
                                                    </IconButton>
                                                )}
                                            </span>
                                        </b>
                                    </Typography>
                                    <ChannelStatus $isActive={isActive}>
                                        {isActive ? t(TK.active) : t(TK.inactive)}
                                    </ChannelStatus>
                                    {isWhatsApp && isWhatsAppCheckboxDisabled && (
                                        <ChannelWarning onClick={(event: React.MouseEvent<HTMLSpanElement>) => event.stopPropagation()}>
                                            {t(TK.noWhatsAppQuotaAvailableAddMoreIn)}{' '}
                                            <MySubscriptionLink to={paths.mySubscription()} onClick={event => event.stopPropagation()}>
                                                {t(TK.mySubscription)}
                                            </MySubscriptionLink>.
                                        </ChannelWarning>
                                    )}
                                </ChannelInfo>
                            </ChannelHeader>
                            {isActive && isExpanded && (
                                <ChannelContent>
                                    <Typography size="large"><b>{t(TK.confirmationMessage)}</b></Typography>
                                    {channel === CommunicationChannel.WhatsApp && (
                                        <Typography size="smaller" style={{color: 'var(--text-muted)'}}>
                                            {t(TK.whatsAppTemplateMetaWarning)}
                                        </Typography>
                                    )}
                                    <PlaceholderTextField
                                        id={`confirmation-${channel}-message`}
                                        label={t(TK.message)}
                                        value={channel === CommunicationChannel.WhatsApp
                                            ? whatsAppConfirmationDefaultTemplate
                                            : appointmentConfirmation.message ?? ''}
                                        type='textarea'
                                        disabled={channel === CommunicationChannel.WhatsApp}
                                        maxLength={500}
                                        onChange={message => {
                                            if (channel === CommunicationChannel.WhatsApp) return;
                                            updateAppointmentConfirmation({ message });
                                        }}
                                    />
                                    {channel === CommunicationChannel.SMS && confirmationSmsNrSegments > 1 && (
                                        <Typography size='small' style={{color: 'var(--danger-color)'}}>
                                            {t(TK.smsSegmentsWarning)} <b>{t(TK.smsNumberOfSegments)}: {confirmationSmsNrSegments}</b>
                                        </Typography>
                                    )}

                                    <NumberInputField
                                        prepend={t(TK.howMuchTimeBefore)}
                                        append={t(TK.hours)}
                                        value={appointmentConfirmation.hoursBeforeAppointment ?? 24}
                                        onChange={hoursBeforeAppointment => {
                                            const value = Math.max(1, hoursBeforeAppointment ?? 24);
                                            updateAppointmentConfirmation({ hoursBeforeAppointment: value });
                                        }}
                                    />

                                    <Typography size="large"><b>{t(TK.confirmationAllowedResponses)}</b></Typography>
                                    <TextInputField
                                        label={t(TK.confirmationAcceptResponses)}
                                        helpText={t(TK.confirmationAcceptResponsesHelp)}
                                        value={isWhatsApp
                                            ? confirmationDefaults.confirm.join(', ')
                                            : appointmentConfirmation.responses?.confirm?.join(', ') ?? ''}
                                        disabled={isWhatsApp}
                                        onChange={value => updateResponses('confirm', value)}
                                    />
                                    <TextInputField
                                        label={t(TK.confirmationRejectResponses)}
                                        helpText={t(TK.confirmationRejectResponsesHelp)}
                                        value={isWhatsApp
                                            ? t(TK.confirmationResponseNo)
                                            : appointmentConfirmation.responses?.reject?.join(', ') ?? ''}
                                        disabled={isWhatsApp}
                                        onChange={value => updateResponses('reject', value)}
                                    />

                                    <Typography size="large"><b>{t(TK.testReminder)}</b></Typography>
                                    <TestSection>
                                        <TextInputField
                                            style={{flexGrow: 1, minWidth: '200px'}}
                                            value={phoneNumberTest}
                                            onChange={setPhoneTest}
                                            placeholder="+351910000000"
                                        />
                                        {channel === CommunicationChannel.SMS && (
                                            <AppButton disabled={testing} style={{whiteSpace: 'nowrap'}} onClick={handleTestConfirmationSms}>
                                                {getMessageIcon('sms', {style: {marginRight: 'var(--spacer-XXS)'}})}
                                                {t(TK.testSms)}
                                            </AppButton>
                                        )}
                                        {channel === CommunicationChannel.WhatsApp && (
                                            <AppButton disabled={testing} style={{whiteSpace: 'nowrap'}} onClick={() => handleTestWhatsApp(true)}>
                                                {getMessageIcon('whatsapp', {style: {marginRight: 'var(--spacer-XXS)'}})}
                                                {t(TK.testWhatsApp)}
                                            </AppButton>
                                        )}
                                    </TestSection>
                                </ChannelContent>
                            )}
                        </ChannelCard>
                    )
                })}
            </div>

            <Dialog open={whatsAppHelpContext !== null} onClose={() => setWhatsAppHelpContext(null)}>
                <DialogTitle>{t(TK.appointmentConfirmationRequest)}</DialogTitle>
                <DialogContent>
                    <div>
                        {whatsAppDisclaimerStart} (+351936385141) {whatsAppDisclaimerEnd}
                    </div>
                    <div style={{marginTop: 'var(--spacer-XS)'}}>
                        <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">{t(TK.privacyPolicy)}</a>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ConfirmationTab;
